import React, { createContext, useContext, useState, useCallback } from 'react';
import type { Product } from './products';

export interface CartItem {
  product: Product;
  quantity: number;
  /** Unique key per variant line: SKU or productId:language */
  lineKey: string;
}

/** Derive a stable line key from a product — unique per language variant */
function deriveLineKey(product: Product): string {
  // SKU is the most reliable unique key per variant
  if (product.sku) return product.sku;
  // Fallback: combine id + language
  const lang = product.language || 'unknown';
  return `${product.id}:${lang}`;
}

/** Language display helpers */
export const LANG_BADGES: Record<string, { flag: string; label: string }> = {
  Japonais: { flag: '🇯🇵', label: 'Japonais' },
  JPN: { flag: '🇯🇵', label: 'Japonais' },
  JP: { flag: '🇯🇵', label: 'Japonais' },
  Anglais: { flag: '🇬🇧', label: 'Anglais (UK)' },
  EN: { flag: '🇬🇧', label: 'Anglais (UK)' },
  Français: { flag: '🇫🇷', label: 'Français' },
  FR: { flag: '🇫🇷', label: 'Français' },
};

export function getLangBadge(language?: string): { flag: string; label: string } | null {
  if (!language) return null;
  return LANG_BADGES[language] || { flag: '🌐', label: language };
}

interface CartContextType {
  items: CartItem[];
  addItem: (product: Product, qty?: number) => void;
  removeItem: (lineKey: string) => void;
  updateQuantity: (lineKey: string, qty: number) => void;
  clearCart: () => void;
  total: number;
  itemCount: number;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  const addItem = useCallback((product: Product, qty = 1) => {
    const key = deriveLineKey(product);
    setItems(prev => {
      const existing = prev.find(i => i.lineKey === key);
      if (existing) {
        return prev.map(i =>
          i.lineKey === key
            ? { ...i, quantity: Math.min(i.quantity + qty, i.product.stock) }
            : i
        );
      }
      return [...prev, { product, quantity: qty, lineKey: key }];
    });
    setIsOpen(true);
  }, []);

  const removeItem = useCallback((lineKey: string) => {
    setItems(prev => prev.filter(i => i.lineKey !== lineKey));
  }, []);

  const updateQuantity = useCallback((lineKey: string, qty: number) => {
    setItems(prev => {
      if (qty <= 0) return prev.filter(i => i.lineKey !== lineKey);
      return prev.map(i =>
        i.lineKey === lineKey ? { ...i, quantity: Math.min(qty, i.product.stock) } : i
      );
    });
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const total = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);
  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, addItem, removeItem, updateQuantity, clearCart,
      total, itemCount, isOpen, setIsOpen,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};